
import { useState, useEffect } from 'react';
import { useUserProfileImages } from '@/hooks/profile';
import { useUser } from '@/hooks/auth/use-user';
import { LightboxImage } from '@/components/image-lightbox';
import { ProfileAvatarState } from './types';

interface UseProfileAvatarStateProps {
  profilePictureUrl?: string | null;
}

export const useProfileAvatarState = ({ profilePictureUrl }: UseProfileAvatarStateProps) => {
  const [state, setState] = useState<ProfileAvatarState>({
    isLightboxOpen: false,
    showDeleteConfirmation: false,
    currentImage: null,
    isSettingProfilePic: false,
    isCurrentImageActiveProfile: false
  });

  const { images, isLoading: isLoadingImages, deleteImage, isDeleting } = useUserProfileImages();
  const { user } = useUser();

  // Pure reactive state management - always sync currentImage with the actual profile picture URL
  useEffect(() => {
    console.log('[ProfileAvatarState] Syncing currentImage with profilePictureUrl:', profilePictureUrl);
    console.log('[ProfileAvatarState] Available images count:', images.length);
    
    if (!images.length) {
      // No images available - clear current image
      setState(prev => ({
        ...prev,
        currentImage: null,
        isCurrentImageActiveProfile: false
      }));
      return;
    }

    let newCurrentImage: LightboxImage | null = null;
    let isActiveProfile = false;

    if (profilePictureUrl) {
      // Try to find exact match for profile picture URL
      const matchingImage = images.find(img => img.url === profilePictureUrl);
      
      if (matchingImage) {
        // Perfect match found
        newCurrentImage = {
          id: matchingImage.id,
          url: matchingImage.url,
          alt: 'Profile Picture'
        };
        isActiveProfile = true;
        console.log('[ProfileAvatarState] Found exact match for profile URL');
      } else {
        // Profile URL set but no matching image found (during upload/generation)
        // Use the most recent image as it's likely the newly uploaded one
        const mostRecentImage = images[0];
        newCurrentImage = {
          id: mostRecentImage.id,
          url: mostRecentImage.url,
          alt: 'Profile Picture'
        };
        isActiveProfile = false; // Not active until DB update completes
        console.log('[ProfileAvatarState] Profile URL not matched, using most recent image');
      }
    } else {
      // No profile picture set - use first available image
      const firstImage = images[0];
      newCurrentImage = {
        id: firstImage.id,
        url: firstImage.url,
        alt: 'Profile Picture'
      };
      isActiveProfile = false;
      console.log('[ProfileAvatarState] No profile URL set, using first available image');
    }

    // Update state with new current image
    setState(prev => ({
      ...prev,
      currentImage: newCurrentImage,
      isCurrentImageActiveProfile: isActiveProfile
    }));
    
  }, [profilePictureUrl, images]); // React to both profile URL and images changes

  // Listen for profile picture update events to force refresh
  useEffect(() => {
    const handleProfilePictureUpdate = (event: CustomEvent) => {
      console.log('[ProfileAvatarState] Profile picture update event received:', event.detail);
      const newProfileUrl = event.detail.url;
      
      // Find the image that matches the new profile URL
      if (newProfileUrl && images.length > 0) {
        const updatedProfileImage = images.find(img => img.url === newProfileUrl);
        
        if (updatedProfileImage) {
          const newCurrentImage = {
            id: updatedProfileImage.id,
            url: newProfileUrl,
            alt: 'Profile Picture'
          };
          
          console.log('[ProfileAvatarState] Event handler updating currentImage:', newCurrentImage);
          setState(prev => ({
            ...prev,
            currentImage: newCurrentImage,
            isCurrentImageActiveProfile: true
          }));
        } else {
          console.log('[ProfileAvatarState] Event received but no matching image found yet');
        }
      }
    };
    
    document.addEventListener('profile-picture-updated', handleProfilePictureUpdate as EventListener);
    
    return () => {
      document.removeEventListener('profile-picture-updated', handleProfilePictureUpdate as EventListener);
    };
  }, [images]);

  return {
    state,
    setState,
    images,
    isLoadingImages,
    deleteImage,
    isDeleting,
    user
  };
};
